/**
 * Items / Shipping / Taxes / total table used by both PaymentCard and RefundCard.
 *
 * The two callers differ only in the section label ("Total Order Amount" vs "Total Refund Amount"),
 * the final-row label ("Order Total" vs "Refund Total"), and an optional sign prefix on every value
 * (refunds render as negatives). Everything else is identical, so the layout lives here once.
 */
import React from "react";
import { createStyles } from "@mantine/core";
import { tokens } from "@blurb/codex-react";
import { GraphQL } from "@rpi/openapi-api";
import { Label } from "../../../../components/Typography";
import { CardSection } from "./OrderDetailCard";
import { PricingLineItem } from "./PricingLineItem";
import { flexColumn, getCurrencyLabel } from "./shared";

const useStyles = createStyles(() => ({
  lineItems: {
    ...flexColumn,
    gap: tokens.spacing[1],
    paddingLeft: tokens.spacing[4],
  },
}));

export type BreakdownData = {
  currency: GraphQL.SupportedCurrency | null | undefined;
  itemsTotal: number | null | undefined;
  shippingTotal: number | null | undefined;
  taxesTotal: number | null | undefined;
  total: number | null | undefined;
};

interface BreakdownTableProps {
  data: BreakdownData;
  sectionLabel: string;
  totalLabel: string;
  prefix?: string;
}

export const BreakdownTable: React.FC<BreakdownTableProps> = ({ data, sectionLabel, totalLabel, prefix }) => {
  const { classes } = useStyles();
  return (
    <CardSection>
      <Label>
        {sectionLabel} ({getCurrencyLabel(data.currency)})
      </Label>
      <div className={classes.lineItems}>
        <PricingLineItem label="Items" currency={data.currency} amount={data.itemsTotal} prefix={prefix} />
        <PricingLineItem label="Shipping" currency={data.currency} amount={data.shippingTotal} prefix={prefix} />
        <PricingLineItem
          label="Taxes"
          currency={data.currency}
          amount={data.taxesTotal}
          border="solid"
          prefix={prefix}
        />
        <PricingLineItem
          label={totalLabel}
          currency={data.currency}
          amount={data.total}
          border="none"
          prefix={prefix}
        />
      </div>
    </CardSection>
  );
};
